import type { CanvasObject, PenStrokeCanvasObject } from "./canvas-object";

// キャンバスの基準寸法（中サイズ = 10K）。全プリセットの固定中心 (CANVAS_WIDTH_PX/2,
// CANVAS_HEIGHT_PX/2) の基準であり、既存キャンバス（サイズ未指定）の既定サイズでもある。
export const CANVAS_WIDTH_PX = 10240;
export const CANVAS_HEIGHT_PX = 5760;

// キャンバスサイズのプリセット（小 / 中 / 大）。いずれも 16:9。中(10K)を既定とする。
export type CanvasSizePreset = "s" | "m" | "l";
export const DEFAULT_CANVAS_SIZE_PRESET: CanvasSizePreset = "m";
export const CANVAS_SIZE_PRESETS: Record<
  CanvasSizePreset,
  { readonly width: number; readonly height: number }
> = {
  s: { width: 7680, height: 4320 }, // 8K
  m: { width: CANVAS_WIDTH_PX, height: CANVAS_HEIGHT_PX }, // 10K（既定）
  l: { width: 12288, height: 6912 }, // 12K
};

// 永続値・op から受け取ったサイズ指定を安全に解釈する（未知値 / undefined は既定=中）。
export function normalizeCanvasSizePreset(value: string | null | undefined): CanvasSizePreset {
  return value === "s" || value === "m" || value === "l" ? value : DEFAULT_CANVAS_SIZE_PRESET;
}

// プリセットからキャンバス矩形（原点 + 寸法）を導出する。
// 中(10K)の中心 (CANVAS_WIDTH_PX/2, CANVAS_HEIGHT_PX/2) を固定中心とし、そこを基準に拡張 / 収縮する。
// これにより「中心基準のリサイズ」を図形を一切動かさずに実現でき、既存の中サイズは原点 (0,0) のまま
// （後方互換）。大サイズでは原点が負値になり、キャンバス矩形が中心の外側へ広がる。
export function resolveCanvasRect(preset: CanvasSizePreset): CanvasFrame {
  const { width, height } = CANVAS_SIZE_PRESETS[preset];
  return {
    x: (CANVAS_WIDTH_PX - width) / 2,
    y: (CANVAS_HEIGHT_PX - height) / 2,
    w: width,
    h: height,
  };
}

// 1 キャンバスあたりのオブジェクト数上限。UI と API で同じ値を参照する。
export const CANVAS_OBJECT_SOFT_LIMIT = 5_000;
export const CANVAS_OBJECT_HARD_LIMIT = 10_000;

/**
 * zOrder の永続化上限（絶対値）。セクション帯の z-index バンドオフセット（±10,000,000。
 * Web レンダリングの SECTION_Z_BAND_OFFSET）を侵して「セクションは常に通常オブジェクトの背面」
 * という不変条件を崩せないようにするための制約。サーバ検証（api の ObjectSchema /
 * TransformSchema）と、Web / MCP の zOrder 採番・入口クランプで同じ値を共有する。
 */
export const CANVAS_OBJECT_Z_ORDER_LIMIT = 1_000_000;

/**
 * zOrder を永続化上限（±CANVAS_OBJECT_Z_ORDER_LIMIT）内へ丸める。
 * 旧スキーマ時代の範囲外 zOrder を持つ既存データがあるキャンバスでも、自動採番
 * （既存 max+1 / min-1）に基づく作成・前面背面移動がサーバ検証で拒否されないようにする。
 */
export function clampZOrderForPersist(zOrder: number): number {
  return Math.max(
    -CANVAS_OBJECT_Z_ORDER_LIMIT,
    Math.min(CANVAS_OBJECT_Z_ORDER_LIMIT, zOrder)
  );
}

export interface CanvasPoint {
  readonly x: number;
  readonly y: number;
}

export interface CanvasFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export function isPointInsideCanvas(point: CanvasPoint): boolean {
  return (
    Number.isFinite(point.x) &&
    Number.isFinite(point.y) &&
    point.x >= 0 &&
    point.x <= CANVAS_WIDTH_PX &&
    point.y >= 0 &&
    point.y <= CANVAS_HEIGHT_PX
  );
}

// 当初仕様（キャンバス外にも配置可）へ戻すため、点の x/y はキャンバス矩形へ丸めず素通しする。
// NaN/Infinity の混入だけを防ぐサニタイズに徹する（境界クランプは行わない）。
export function clampPointToCanvas<T extends CanvasPoint>(point: T): T {
  return {
    ...point,
    x: finiteOrZero(point.x),
    y: finiteOrZero(point.y),
  };
}

export function isFrameInsideCanvas(frame: CanvasFrame): boolean {
  return (
    Number.isFinite(frame.x) &&
    Number.isFinite(frame.y) &&
    Number.isFinite(frame.w) &&
    Number.isFinite(frame.h) &&
    frame.w >= 0 &&
    frame.h >= 0 &&
    frame.x >= 0 &&
    frame.y >= 0 &&
    frame.x + frame.w <= CANVAS_WIDTH_PX &&
    frame.y + frame.h <= CANVAS_HEIGHT_PX
  );
}

// 当初仕様（キャンバス外にも配置可）へ戻すため、x/y はキャンバス矩形へ丸めず素通しする。
// w/h の非負・有限性の正規化のみ行う（NaN/Infinity/負のサイズの永続化を防ぐ）。境界クランプは行わない。
export function clampFrameToCanvas<T extends CanvasFrame>(frame: T): T {
  return {
    ...frame,
    x: finiteOrZero(frame.x),
    y: finiteOrZero(frame.y),
    w: Math.max(0, finiteOrZero(frame.w)),
    h: Math.max(0, finiteOrZero(frame.h)),
  };
}

export function isCanvasObjectInsideCanvas(object: CanvasObject): boolean {
  if (object.type === "pen-stroke") {
    return (
      isFrameInsideCanvas(object) &&
      object.points.every((point) => isPointInsideCanvas(point))
    );
  }
  return isFrameInsideCanvas(object);
}

export function clampCanvasObjectToCanvas<T extends CanvasObject>(object: T): T {
  if (object.type === "pen-stroke") {
    return clipPenStrokeObjectToCanvas(object) as T;
  }
  return {
    ...object,
    ...clampFrameToCanvas(object),
  };
}

export function clipPenStrokeObjectToCanvas(
  object: PenStrokeCanvasObject
): PenStrokeCanvasObject {
  const points = object.points.map((point) => clampPointToCanvas(point));
  return {
    ...object,
    ...computePenStrokeFrame(points, object.thickness),
    points,
  };
}

export function computePenStrokeFrame(
  points: readonly CanvasPoint[],
  thickness: number
): CanvasFrame {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const first = points[0];
  if (!first) return { x: 0, y: 0, w: 0, h: 0 };

  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }

  const safeThickness = Number.isFinite(thickness) ? Math.max(0, thickness) : 0;
  const pad = safeThickness;
  // 当初仕様（キャンバス外にも配置可）へ戻すため、外接矩形をキャンバス矩形へ丸めない。
  // 実際の点の範囲（負値・キャンバス外を含む）に thickness 分の余白を足した矩形を返す。
  const left = finiteOrZero(minX - pad);
  const top = finiteOrZero(minY - pad);
  const right = finiteOrZero(maxX + pad);
  const bottom = finiteOrZero(maxY + pad);
  return {
    x: left,
    y: top,
    w: Math.max(0, right - left),
    h: Math.max(0, bottom - top),
  };
}

// 有限値でなければ 0 に正規化する（NaN/Infinity の永続化を防ぐ）。
function finiteOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
